import React, { useState } from 'react';
import { theme, cardStyle } from '../theme';

/**
 * BeforeAfterCompare — side-by-side photo comparison.
 *
 * Props:
 *   documents  — array of document objects with tag and filePath
 *   UPLOAD_BASE — base URL for uploaded files
 */
const BeforeAfterCompare = ({ documents, UPLOAD_BASE }) => {
  const [selectedBefore, setSelectedBefore] = useState(0);
  const [selectedAfter, setSelectedAfter] = useState(0);
  const [viewMode, setViewMode] = useState('side'); // 'side' | 'toggle'
  const [showToggle, setShowToggle] = useState('before');

  const beforeDocs = (documents || []).filter(d =>
    (d.tags || []).some(t => t.toLowerCase() === 'before') ||
    (d.tag || '').toLowerCase() === 'before'
  );
  const afterDocs = (documents || []).filter(d =>
    (d.tags || []).some(t => t.toLowerCase() === 'after') ||
    (d.tag || '').toLowerCase() === 'after'
  );

  if (beforeDocs.length === 0 && afterDocs.length === 0) return null;

  const imgStyle = {
    width: '100%', height: '280px', objectFit: 'cover',
    borderRadius: theme.radiusSm, border: `1px solid ${theme.border}`,
    backgroundColor: '#f5f5f5',
  };

  const getUrl = (doc) => {
    if (!doc) return '';
    const path = doc.filePath || doc.url || '';
    if (path.startsWith('http')) return path;
    return `${UPLOAD_BASE}/${path}`;
  };

  return (
    <div style={{ ...cardStyle, marginBottom: '16px' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '14px' }}>
        <h4 style={{ margin: 0, color: theme.navy, fontSize: '1rem' }}>Before / After Comparison</h4>
        <div style={{ display: 'flex', gap: '6px' }}>
          <button onClick={() => setViewMode('side')}
            style={{
              padding: '4px 12px', borderRadius: '6px', fontSize: '0.78rem', fontWeight: '600', cursor: 'pointer',
              border: `1px solid ${theme.primary}`,
              backgroundColor: viewMode === 'side' ? theme.primary : 'white',
              color: viewMode === 'side' ? 'white' : theme.primary,
            }}>Side by Side</button>
          <button onClick={() => setViewMode('toggle')}
            style={{
              padding: '4px 12px', borderRadius: '6px', fontSize: '0.78rem', fontWeight: '600', cursor: 'pointer',
              border: `1px solid ${theme.primary}`,
              backgroundColor: viewMode === 'toggle' ? theme.primary : 'white',
              color: viewMode === 'toggle' ? 'white' : theme.primary,
            }}>Toggle</button>
        </div>
      </div>

      {viewMode === 'side' ? (
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
          {/* Before column */}
          <div>
            <div style={{ fontSize: '0.82rem', fontWeight: '700', color: theme.danger, marginBottom: '6px', textTransform: 'uppercase' }}>Before</div>
            {beforeDocs.length > 0 ? (
              <>
                <img src={getUrl(beforeDocs[selectedBefore])} alt="Before" style={imgStyle} />
                {beforeDocs.length > 1 && (
                  <div style={{ display: 'flex', gap: '4px', marginTop: '6px', overflowX: 'auto' }}>
                    {beforeDocs.map((d, i) => (
                      <img key={i} src={getUrl(d)} alt={`Before ${i + 1}`}
                        onClick={() => setSelectedBefore(i)}
                        style={{
                          width: '50px', height: '50px', objectFit: 'cover', borderRadius: '4px', cursor: 'pointer',
                          border: i === selectedBefore ? `2px solid ${theme.primary}` : `1px solid ${theme.border}`,
                        }} />
                    ))}
                  </div>
                )}
              </>
            ) : (
              <div style={{ height: '280px', display: 'flex', alignItems: 'center', justifyContent: 'center', backgroundColor: '#fafafa', borderRadius: theme.radiusSm, color: '#bbb', border: `1px dashed ${theme.border}` }}>
                No "before" photos tagged
              </div>
            )}
          </div>

          {/* After column */}
          <div>
            <div style={{ fontSize: '0.82rem', fontWeight: '700', color: theme.success, marginBottom: '6px', textTransform: 'uppercase' }}>After</div>
            {afterDocs.length > 0 ? (
              <>
                <img src={getUrl(afterDocs[selectedAfter])} alt="After" style={imgStyle} />
                {afterDocs.length > 1 && (
                  <div style={{ display: 'flex', gap: '4px', marginTop: '6px', overflowX: 'auto' }}>
                    {afterDocs.map((d, i) => (
                      <img key={i} src={getUrl(d)} alt={`After ${i + 1}`}
                        onClick={() => setSelectedAfter(i)}
                        style={{
                          width: '50px', height: '50px', objectFit: 'cover', borderRadius: '4px', cursor: 'pointer',
                          border: i === selectedAfter ? `2px solid ${theme.primary}` : `1px solid ${theme.border}`,
                        }} />
                    ))}
                  </div>
                )}
              </>
            ) : (
              <div style={{ height: '280px', display: 'flex', alignItems: 'center', justifyContent: 'center', backgroundColor: '#fafafa', borderRadius: theme.radiusSm, color: '#bbb', border: `1px dashed ${theme.border}` }}>
                No "after" photos tagged
              </div>
            )}
          </div>
        </div>
      ) : (
        /* Toggle mode */
        <div>
          <div style={{ display: 'flex', gap: '8px', marginBottom: '10px', justifyContent: 'center' }}>
            <button onClick={() => setShowToggle('before')}
              style={{
                padding: '6px 20px', borderRadius: '6px', fontWeight: '700', cursor: 'pointer', fontSize: '0.85rem',
                border: 'none',
                backgroundColor: showToggle === 'before' ? theme.danger : '#f0f0f0',
                color: showToggle === 'before' ? 'white' : '#555',
              }}>Before</button>
            <button onClick={() => setShowToggle('after')}
              style={{
                padding: '6px 20px', borderRadius: '6px', fontWeight: '700', cursor: 'pointer', fontSize: '0.85rem',
                border: 'none',
                backgroundColor: showToggle === 'after' ? theme.success : '#f0f0f0',
                color: showToggle === 'after' ? 'white' : '#555',
              }}>After</button>
          </div>
          {showToggle === 'before' && beforeDocs.length > 0 && (
            <img src={getUrl(beforeDocs[selectedBefore])} alt="Before" style={{ ...imgStyle, height: '360px' }} />
          )}
          {showToggle === 'after' && afterDocs.length > 0 && (
            <img src={getUrl(afterDocs[selectedAfter])} alt="After" style={{ ...imgStyle, height: '360px' }} />
          )}
          {showToggle === 'before' && beforeDocs.length === 0 && (
            <div style={{ height: '200px', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#bbb' }}>No "before" photos</div>
          )}
          {showToggle === 'after' && afterDocs.length === 0 && (
            <div style={{ height: '200px', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#bbb' }}>No "after" photos</div>
          )}
        </div>
      )}
    </div>
  );
};

export default BeforeAfterCompare;
